import { applyMask } from '@/utils/applyMask';
import { removeNonDigits } from '@/utils/removeNonDigits';
import { type FormEvent, useState } from 'react';
import type { GiftCard, GiftCardsData } from '../MyAccount.types';
import { postAccountAction } from '../shared/postAccountAction';

interface GiftCardsPageProps {
  data: GiftCardsData;
}

const cardNumberRegex = /(\d{1,4})(\d{0,4})(\d{0,4})(\d{0,4})/;
const cardNumberMask = '$1 $2 $3 $4';

export const GiftCardsPage = ({ data }: GiftCardsPageProps) => {
  const [giftCards, setGiftCards] = useState<GiftCard[]>(data.giftCards);
  const [showForm, setShowForm] = useState(false);
  const [cardNumber, setCardNumber] = useState('');
  const [cardPin, setCardPin] = useState('');
  const [isPending, setIsPending] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const postAction = async (url: string, body: Record<string, unknown>) => {
    setIsPending(true);
    try {
      return await postAccountAction(url, body);
    } finally {
      setIsPending(false);
    }
  };

  const handleAdd = async (e: FormEvent) => {
    e.preventDefault();
    setFieldErrors({});
    const result = await postAction(data.endpoints.add, {
      giftCardNumber: removeNonDigits(cardNumber),
      giftCardPin: cardPin,
    });
    if (result.success && result.giftCards) {
      setGiftCards(result.giftCards);
      setCardNumber('');
      setCardPin('');
      setShowForm(false);
    } else {
      const errors = result.errors ?? { giftCardNumber: result.message ?? 'Failed to add gift card' };
      setFieldErrors(errors);
    }
  };

  const handleCardNumberChange = (value: string) => {
    const cleanedValue = removeNonDigits(value);
    const maskedValue = applyMask({
      mask: cardNumberMask,
      regex: cardNumberRegex,
      value: cleanedValue,
    });

    setCardNumber(maskedValue);
    if (fieldErrors.giftCardNumber) {
      setFieldErrors(prev => ({ ...prev, giftCardNumber: '' }));
    }
  };

  const handleRemove = async (number: string) => {
    const result = await postAction(data.endpoints.remove, { giftCardNumber: number });
    if (result.giftCards) setGiftCards(result.giftCards);
  };

  if (!data.giftCardsEnabled) {
    return (
      <>
        <div className="ma-head"><h3>Gift cards</h3></div>
        <p className="ma-hint">We're sorry, but temporarily you're unable to manage your gift cards. Please try again later.</p>
      </>
    );
  }

  return (
    <>
      <div className="ma-head"><h3>Gift cards</h3></div>
      <p className="ma-hint">Add and manage your Eason gift cards.</p>

      {showForm ? (
        <div className="ma-card">
          <form onSubmit={handleAdd}>
            <div className="ma-field">
              <label className="ma-label" htmlFor="gc-number">Gift card number</label>
              <input
                id="gc-number"
                className={`ma-input${fieldErrors.giftCardNumber ? ' has-error' : ''}`}
                inputMode="numeric"
                maxLength={19}
                onChange={e => handleCardNumberChange(e.target.value)}
                pattern="^[\d\s]{19}$"
                required
                type="tel"
                value={cardNumber}
              />
              {fieldErrors.giftCardNumber && <span className="ma-field-error">{fieldErrors.giftCardNumber}</span>}
            </div>
            <div className="ma-field">
              <label className="ma-label" htmlFor="gc-pin">8-digit pin code</label>
              <input id="gc-pin" className={`ma-input${fieldErrors.giftCardPin ? ' has-error' : ''}`} type="text" value={cardPin} maxLength={8} onChange={e => { setCardPin(e.target.value); if (fieldErrors.giftCardPin) setFieldErrors(prev => ({ ...prev, giftCardPin: '' })); }} required />
              {fieldErrors.giftCardPin && <span className="ma-field-error">{fieldErrors.giftCardPin}</span>}
            </div>
            <div style={{ display: 'flex', gap: '12px', marginTop: '24px', alignItems: 'center' }}>
              <button type="submit" className="ma-btn-primary" style={{ marginTop: 0 }} disabled={isPending}>Add gift card</button>
              <button type="button" className="ma-btn-secondary" onClick={() => { setShowForm(false); setFieldErrors({}); }}>Cancel</button>
            </div>
          </form>
        </div>
      ) : (
        <>
          <button type="button" className="addr-add gc-add-btn" onClick={() => setShowForm(true)}>
            <span className="addr-add-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            </span>
            Add a gift card
          </button>

          {giftCards.length > 0 && (
            <div className="gc-list" style={{ marginTop: '20px' }}>
              {giftCards.map(gc => (
                <div key={gc.giftCardNumber} className="gc-item">
                  <div className="gc-info">
                    <span className="gc-number"><span className="gc-label">Card Number</span> {gc.giftCardNumber}</span>
                    {gc.balance != null && <span className="gc-balance"><span className="gc-label">Balance</span> €{parseFloat(gc.balance).toFixed(2)}</span>}
                  </div>
                  <button type="button" className="ma-btn-ghost ma-btn-destructive" onClick={() => handleRemove(gc.giftCardNumber)}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                    Remove
                  </button>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </>
  );
};
